import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Wand2, User, Building2, Users, Award, Trophy, Tag, Target, MessageSquare } from 'lucide-react';
import { Button } from '../../../shared/components/ui/button';
import { Input } from '../../../shared/components/ui/input';
import { Label } from '../../../shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../shared/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../shared/components/ui/card';

const goalOptions = [
  { value: 'get-clients', label: 'Get Clients' },
  { value: 'get-job-offers', label: 'Get Job Offers' },
  { value: 'build-authority', label: 'Build Authority' },
  { value: 'grow-network', label: 'Grow Network' },
];

const toneOptions = [
  { value: 'professional', label: 'Professional' },
  { value: 'bold', label: 'Bold' },
  { value: 'simple-clear', label: 'Simple & Clear' },
  { value: 'authority-driven', label: 'Authority-Driven' },
  { value: 'sales-focused', label: 'Sales-Focused' },
  { value: 'friendly', label: 'Friendly' },
];

const inputFields = [
  { id: 'role', label: 'Your Role / Job Title', placeholder: 'e.g., Marketing Director, SaaS Founder, UX Designer', icon: User },
  { id: 'industry', label: 'Your Industry', placeholder: 'e.g., Technology, Healthcare, Finance, Marketing', icon: Building2 },
  { id: 'targetAudience', label: 'Who You Serve / Your Target Audience', placeholder: 'e.g., B2B SaaS Startups, Fortune 500 Companies, SMBs', icon: Users },
  { id: 'strengths', label: 'Your Top Strengths or Skills', placeholder: 'e.g., Lead Generation, Data Analysis, Strategic Planning', icon: Award },
  { id: 'achievements', label: 'Your Achievements or Outcomes', placeholder: 'e.g., Grew revenue 300%, Managed $5M budgets, 10+ years experience', icon: Trophy },
  { id: 'keywords', label: 'Your Keywords', placeholder: 'e.g., Growth Marketing, SEO, LinkedIn Expert, B2B Sales', icon: Tag },
];

export const InputForm = ({ onGenerate, isLoading }) => {
  const [formData, setFormData] = useState({
    role: '',
    industry: '',
    targetAudience: '',
    strengths: '',
    achievements: '',
    keywords: '',
    goal: '',
    tone: '',
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onGenerate(formData);
  };

  const isFormValid = formData.role && formData.industry && formData.goal && formData.tone;

  return (
    <section id="generator" className="py-12 md:py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Card className="shadow-xl border-border/50">
            <CardHeader className="text-center pb-2">
              <CardTitle className="font-display text-2xl md:text-3xl text-foreground">
                Enter Your Details
              </CardTitle>
              <CardDescription className="text-base">
                Fill in your professional information to generate personalized headlines
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Text Input Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {inputFields.map((field) => (
                    <div key={field.id} className="space-y-2">
                      <Label htmlFor={field.id} className="flex items-center gap-2">
                        <field.icon className="w-4 h-4 text-primary" />
                        {field.label}
                      </Label>
                      <Input
                        id={field.id}
                        placeholder={field.placeholder}
                        value={formData[field.id]}
                        onChange={(e) => handleInputChange(field.id, e.target.value)}
                      />
                    </div>
                  ))}
                </div>

                {/* Dropdown Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="goal" className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-primary" />
                      Your LinkedIn Goal
                    </Label>
                    <Select
                      value={formData.goal}
                      onValueChange={(value) => handleInputChange('goal', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select your primary goal" />
                      </SelectTrigger>
                      <SelectContent>
                        {goalOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tone" className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-primary" />
                      Preferred Tone
                    </Label>
                    <Select
                      value={formData.tone}
                      onValueChange={(value) => handleInputChange('tone', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select your preferred tone" />
                      </SelectTrigger>
                      <SelectContent>
                        {toneOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    variant="primary"
                    size="xl"
                    className="w-full"
                    disabled={!isFormValid || isLoading}
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                        Generating Headlines...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-5 h-5" />
                        Generate Headlines
                      </>
                    )}
                  </Button>
                  {!isFormValid && (
                    <p className="text-sm text-muted-foreground text-center mt-3">
                      Please fill in at least your role, industry, goal, and tone to generate headlines
                    </p>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};
